// Share a generated .docx to the priest, preferring the native Web Share sheet
// (which surfaces WhatsApp as a target on phones) and falling back to a download
// plus a pre-filled wa.me message on desktops that can't share files.
//
// CRITICAL (iOS Safari): navigator.share() must run inside the user-activation of
// the click. Do NOT await anything (blob generation, network) before calling it —
// pass an already-built File. Hence the caller pre-arms the File off-gesture.

type ShareCapableNavigator = Navigator & {
  canShare?: (data?: { files?: File[] }) => boolean;
  share?: (data: { files?: File[]; title?: string; text?: string }) => Promise<void>;
};

export type ShareOutcome = "shared" | "downloaded" | "cancelled";

/**
 * Open the OS share sheet for a file (so phones can send it to WhatsApp / mail /
 * Files), falling back to a plain download where file sharing isn't supported.
 */
export async function shareDocxFile(file: File, title = "Feuille de messe"): Promise<ShareOutcome> {
  const nav = navigator as ShareCapableNavigator;
  if (typeof nav.canShare === "function" && typeof nav.share === "function" && nav.canShare({ files: [file] })) {
    try {
      await nav.share({ files: [file], title });
      return "shared";
    } catch (e: any) {
      if (e?.name === "AbortError") return "cancelled";
    }
  }
  downloadFile(file);
  return "downloaded";
}

function downloadFile(file: File): void {
  const url = URL.createObjectURL(file);
  const a = document.createElement("a");
  a.href = url;
  a.download = file.name;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}

/**
 * Share `file` with `text`. Returns "shared" when the OS share sheet handled it,
 * "cancelled" when the user dismissed it, or "downloaded" when we fell back to a
 * download + opening WhatsApp with the message (the user attaches the file).
 *
 * Call this WITHOUT awaiting anything before it, from a click handler.
 */
export async function shareDocxToWhatsApp(file: File, text: string, title = "Feuille d'animation"): Promise<ShareOutcome> {
  const nav = navigator as ShareCapableNavigator;
  if (typeof nav.canShare === "function" && typeof nav.share === "function" && nav.canShare({ files: [file] })) {
    try {
      await nav.share({ files: [file], title, text });
      return "shared";
    } catch (e: any) {
      if (e?.name === "AbortError") return "cancelled";
      // Any other error → fall through to the download fallback.
    }
  }
  downloadFile(file);
  try {
    window.open("https://wa.me/?text=" + encodeURIComponent(text), "_blank", "noopener");
  } catch {
    /* popup blocked — the download still happened */
  }
  return "downloaded";
}
